import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertBody, AlertIcon, cn, Stack } from 'component-library';
import { HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Logger } from '@/lib/logs/logger';
import { isRestApiServerError } from '@/utils/useRestApiServerErrorHandling';

import { useEditRoadmapNode } from '../hooks/useEditRoadmapNode';

export type UploadRoadmapNodeProps = {
  organizationId: string;
} & HTMLAttributes<HTMLDivElement>;

export const UploadRoadmapNode = ({ organizationId }: UploadRoadmapNodeProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isDropzoneDisabled, setIsDropzoneDisabled] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  const { getRootProps, getInputProps, acceptedFiles, isDragActive, fileRejections } = useDropzone({
    accept: {
      'application/json': ['.json'],
    },
    maxSize: 5 * 1024 * 1024,
    maxFiles: 1,
    disabled: isDropzoneDisabled,
  });

  const { submit, isSubmitting } = useEditRoadmapNode();

  useEffect(() => {
    async function uploadFile() {
      const acceptedFile = acceptedFiles.at(0);
      if (!acceptedFile) return;

      const reader = new FileReader();
      reader.readAsText(acceptedFile);

      let jsonResult = {};
      reader.onload = async () => {
        jsonResult = JSON.parse(reader.result as string);

        try {
          await submit({ organizationId, inputs: jsonResult });

          await queryClient.invalidateQueries({
            queryKey: ['organization', { organization_id: organizationId }, 'roadmap-nodes'],
          });
        } catch (error) {
          if (isRestApiServerError(error) && error.formErrors[0]?.messages[0]) {
            setUploadError(error.formErrors[0].messages[0]);
          } else {
            setUploadError(t('shared.goals.uploadNode.error.unknown-error'));
          }
        }
      };
    }

    uploadFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles]);

  useEffect(
    // eslint-disable-next-line prefer-arrow-callback
    function clearUploadErrors() {
      setUploadError(undefined);
    },
    [acceptedFiles],
  );

  useEffect(() => {
    setIsDropzoneDisabled(isSubmitting);
  }, [isSubmitting]);

  const fileErrors = useMemo(() => {
    return Array.from(
      new Set(
        fileRejections
          .flatMap((fileRejection) => fileRejection.errors)
          .map((error) => {
            const knownErrorCodes = ['file-invalid-type', 'file-too-large', 'too-many-files'] as const;
            const isKnownErrorCode = (errorCode: string): errorCode is (typeof knownErrorCodes)[number] => {
              return knownErrorCodes.some((knownError) => knownError === errorCode);
            };

            if (isKnownErrorCode(error.code)) {
              return t(`shared.goals.uploadNode.error.${error.code}`);
            }

            Logger.errorOnce(
              `Unrecognised error while uploading file. Error code: ${error.code}, Error message: ${error.message}`,
            );
            return t(`shared.goals.uploadNode.error.unknown-error`);
          }),
      ),
    );
  }, [fileRejections, t]);

  return (
    <Stack spacing={6} className='w-full h-full rounded-2xl bg-white-100' data-testid='upload-roadmap-node'>
      <Stack
        {...getRootProps({
          className: cn(
            'relative border border-dashed border-neutral-black-30 p-8 items-center justify-center rounded-2xl flex-1 transition-colors',
            {
              'bg-primary-12': isDragActive,
            },
          ),
        })}
      >
        <input {...getInputProps()} />
        <Stack spacing={2} className='items-center'>
          <Icon icon='ri-file-upload-line' className='text-primary-100' />
          <span className='typography-h4'>{t('shared.goals.uploadNode.filezone.titleDefault')}</span>
          <span className='typography-body1 text-text-secondary transition-colors'>
            {t('shared.goals.uploadNode.filezone.subtitle')}
          </span>
        </Stack>
        {isSubmitting && (
          <Stack
            spacing={4}
            className='absolute typography-body1Semibold text-primary-100 h-full w-full cursor-not-allowed items-center justify-center rounded-2xl bg-white-100'
          >
            {t('shared.goals.uploadNode.uploading')}
          </Stack>
        )}
      </Stack>
      {fileErrors.length > 0 && (
        <Stack spacing={2}>
          {fileErrors.map((error, index) => (
            <Alert key={index} type='error'>
              <AlertIcon>
                <Icon icon='ri-error-warning-line' />
              </AlertIcon>
              <AlertBody>{error}</AlertBody>
            </Alert>
          ))}
        </Stack>
      )}
      {uploadError && (
        <Alert type='error'>
          <AlertIcon>
            <Icon icon='ri-error-warning-line' />
          </AlertIcon>
          <AlertBody>{uploadError}</AlertBody>
        </Alert>
      )}
    </Stack>
  );
};
